module GraphQL.Compound where

import Common

import Control.Monad.Except (ExceptT(..), except, runExceptT)
import Data.Argonaut (fromObject)
import Data.Symbol (class IsSymbol, reflectSymbol)
import Foreign.Object (Object)
import Foreign.Object as O
import GraphQL (query)
import GraphQL.Join (class GraphQL, class JoinableQuery, getDecode)
import GraphQL.Query (Query(..), QueryRec, QueryVar, fromBaseQuery, queryToString)
import GraphQL.Shape (QueryField(..), QueryShape)
import Prim.Row as Row
import Prim.RowList as RL
import Record as Rec
import Type.Prelude (Proxy(..))


-- | A query composed of multiple simple queries
data CompQuery a = CompQuery (QueryRec a)


-- | Combine multiple calls to various GraphQL queries into a single query
-- |
-- | This expects a record where each field is an instance of `SubQueryable`
-- | and produces a query which returns a matching record, but with the values
-- | replaced with the result of each sub-query.
-- |
-- | For example, the input `{ foo: fooQuery, bar: barQuery }` will return
-- | `CompQuery { foo :: FooResult, bar :: BarResult }`
-- |
-- | All variables passed to sub-queries and all decoding behaviors will be
-- | preserved.
composeQueries :: forall q r list. RL.RowToList q list => ComposeQueries q r list =>
    String -> Record q -> CompQuery (Record r)
composeQueries name queries = CompQuery $ fromBaseQuery query vars decode
  where
    vars = cqVars lprox rprox queries
    args = cqArgs lprox rprox queries
    shape = cqShape lprox rprox queries
    decode = cqDecode lprox queries
    -- TODO: Support files and mutations in compound queries
    query = { name, args, shape, files: [], mutation: false }

    lprox = Proxy :: Proxy list
    rprox = Proxy :: Proxy r


instance cqGQL :: GraphQL (CompQuery a) a where
    runGraphQL q'@(CompQuery q@{ vars }) = runExceptT do
        ret <- ExceptT $ query (queryToString q) vars
        except $ getDecode q' ret

instance cqJoin :: JoinableQuery (CompQuery a) a where
    getQuery (CompQuery q@{ vars }) = { query: queryToString q, variables: fromObject vars }
    getDecode (CompQuery { decode }) = decode

-- | Class for queries which can be composed into a `CompQuery`
class SubQueryable q r | q -> r where
    -- | Get the variables in this query as JSON
    subQueryVars :: q -> Object Json
    -- | Get the variables in this query as a list of `QueryArg`s
    subQueryArgs :: q -> Array QueryVar
    -- | Get the shape of the subquery wrapped in a `QCall` field with the arguments
    subQueryShape :: q -> Array QueryField
    -- | Decode the subquery
    subQueryDecode :: q -> Json -> Either ErrorWrapper r

instance querySQ :: SubQueryable (Query r) r where
    subQueryVars (Query { vars }) = vars
    subQueryArgs (Query { args }) = args
    subQueryShape (Query { shape }) = shape
    subQueryDecode (Query { decode }) = decode


-- | Internal class for composing queries.
-- |
-- | Do not implement this type directly. Use `SubQueryable` instead.
class ComposeQueries (q :: Row Type) (r :: Row Type) (list :: RL.RowList Type) | list -> q r where
    cqVars :: Proxy list -> Proxy r -> Record q -> Object Json
    cqArgs :: Proxy list -> Proxy r -> Record q -> Array QueryVar
    cqShape :: Proxy list -> Proxy r -> Record q -> QueryShape
    cqDecode :: Proxy list -> Record q -> Json -> Either ErrorWrapper (Record r)

instance qcCons ::
    ( ComposeQueries qtail rtail tail
    , IsSymbol field
    , Row.Cons field query qtail qrow
    , Row.Lacks field qtail
    , Row.Cons field result rtail rrow
    , Row.Lacks field rtail
    , SubQueryable query result
    ) => ComposeQueries qrow rrow (RL.Cons field query tail) where
        cqVars _ _ q = O.union (subQueryVars val) $
            cqVars (Proxy :: Proxy tail) (Proxy :: Proxy rtail) (Rec.delete field q)
          where
            val = Rec.get field q
            field = Proxy :: Proxy field
        cqArgs _ _ q = (renamed $ subQueryArgs val) <>
            cqArgs (Proxy :: Proxy tail) (Proxy :: Proxy rtail) (Rec.delete field q)
          where
            renamed a = a -- TODO
            val = Rec.get field q
            field = Proxy :: Proxy field
        cqShape _ _ q = (map aliased $ subQueryShape val) <>
            cqShape (Proxy :: Proxy tail) (Proxy :: Proxy rtail) (Rec.delete field q)
          where
            aliased = QAlias $ reflectSymbol field
            val = Rec.get field q
            field = Proxy :: Proxy field
        cqDecode _ q json = do
            rest <- cqDecode (Proxy :: Proxy tail) (Rec.delete field q) json
            inner <- wrapJsonError' json $ decodeJObject json >>= (_ .: "data") >>= (_ .: name)
            rval <- subQueryDecode val inner
            pure $ Rec.insert field rval rest
          where
            val = Rec.get field q
            name = reflectSymbol field
            field = Proxy :: Proxy field
instance cqNil :: ComposeQueries () () RL.Nil where
    cqVars _ _ _ = O.empty
    cqArgs _ _ _ = []
    cqShape _ _ _ = []
    cqDecode _ _ _ = Right {}
